/**
 * POD Print Details Handler
 *
 * A centralized module for managing and accessing print job details
 * across different components of the Print-on-Demand system.
 */

// Singleton instance pattern
const PrintDetailsHandler = (function() {
  // Private storage for print details
  let _printDetails = {
    // Default values
    material: '',
    color: '',
    materialAndColor: '',  // Will be computed
    quality: '',
    supports: '',
    infill: 0,
    weight: 0,
    dimensions: {
      length: 0,
      width: 0,
      height: 0
    },
    price: 0,
    fileId: null
  };

  // Helper function to format dimensions as "L x W x H"
  function formatDimensions() {
    return `${_printDetails.dimensions.length} x ${_printDetails.dimensions.width} x ${_printDetails.dimensions.height}`;
  }

  // Public API
  return {
    /**
     * Initialize or update all print details at once
     * @param {Object} details - Object containing print details
     */
    updateDetails: function(details) {
      // Update only the properties that are provided
      Object.keys(details).forEach(key => {
        if (key === 'dimensions' && typeof details.dimensions === 'object') {
          Object.assign(_printDetails.dimensions, details.dimensions);
        } else {
          _printDetails[key] = details[key];
        }
      });
      
      // Update the combined material and color
      if (details.material || details.color) {
        _printDetails.materialAndColor = `${_printDetails.material} ${_printDetails.color}`.trim();
      }
      
      // Trigger an event for components to update
      document.dispatchEvent(new CustomEvent('printDetailsUpdated', { 
        detail: this.getAllDetails() 
      }));
    },
    
    /**
     * Get all print details
     * @returns {Object} Object containing all print details
     */
    getAllDetails: function() {
      return {
        ..._printDetails,
        formattedDimensions: formatDimensions()
      };
    },
    
    /**
     * Get material name
     * @returns {string} Material name
     */
    getMaterial: function() {
      return _printDetails.material;
    },
    
    /**
     * Get color name
     * @returns {string} Color name
     */
    getColor: function() {
      return _printDetails.color;
    },
    
    /**
     * Get combined material and color
     * @returns {string} Material and color
     */
    getMaterialAndColor: function() {
      return _printDetails.materialAndColor;
    },
    
    /**
     * Get print quality
     * @returns {string} Print quality
     */
    getQuality: function() {
      return _printDetails.quality;
    },
    
    /**
     * Get support selection
     * @returns {string} Support selection
     */
    getSupports: function() {
      return _printDetails.supports;
    },
    
    /**
     * Get infill percentage
     * @returns {number} Infill percentage
     */
    getInfill: function() {
      return _printDetails.infill;
    },
    
    /**
     * Get print weight in grams
     * @returns {number} Print weight in grams
     */
    getWeight: function() {
      return _printDetails.weight;
    },
    
    /**
     * Get dimensions object
     * @returns {Object} Object with length, width, and height
     */
    getDimensions: function() {
      return { ..._printDetails.dimensions };
    },
    
    /**
     * Get formatted dimensions string (L x W x H)
     * @returns {string} Formatted dimensions
     */
    getFormattedDimensions: function() {
      return formatDimensions();
    },
    
    /**
     * Get calculated price
     * @returns {number} Price
     */
    getPrice: function() {
      return _printDetails.price;
    },
    
    /**
     * Get file ID after upload
     * @returns {string|null} File ID or null if not uploaded
     */
    getFileId: function() {
      return _printDetails.fileId;
    },
    
    /**
     * Set file ID after upload
     * @param {string} id - File ID
     */
    setFileId: function(id) {
      _printDetails.fileId = id;
      
      // Trigger an event for components to update
      document.dispatchEvent(new CustomEvent('fileIdUpdated', { 
        detail: id 
      }));
    }
  };
})();

// Make it available globally
window.PrintDetailsHandler = PrintDetailsHandler; 